import { type AbstractMesh, Color3, MeshBuilder, type Node, type Scene, StandardMaterial, TransformNode } from "@babylonjs/core";
import type { AssetInfo } from "../net/api";
import { fitTransform, layoutShelf, SHELF, type ShelfLayout } from "./layout";
import { type LoadedModel, loadGLB, type ModelLoader, roots } from "./models";
import type { BuiltRoom } from "./template";

interface Slot { code: string; holder: TransformNode; model: LoadedModel | null; badge: AbstractMesh | null }

/** The models on the shelf, one page at a time (spec 2o §2.3). */
export class ShelfView {
  private slots: Slot[] = [];
  private assets: AssetInfo[] = [];
  private page = 0;
  private gen = 0; // a load that finishes after the page changed is thrown away
  private hovered: string | null = null;
  private broken = new Set<string>();

  constructor(private readonly scene: Scene, private readonly room: BuiltRoom, private readonly load: ModelLoader = loadGLB) {}

  layout(): ShelfLayout { return layoutShelf(this.assets, this.page); }

  setAssets(list: readonly AssetInfo[]): void {
    this.assets = [...list];
    this.render();
  }

  turn(dir: -1 | 1): void {
    const { page, pages } = this.layout();
    const next = Math.min(Math.max(page + dir, 0), pages - 1);
    if (next === page) return;
    this.page = next;
    this.render();
  }

  /** The asset code of the model a picked mesh belongs to, or null. */
  codeAt(mesh: AbstractMesh | null | undefined): string | null {
    for (let n: Node | null = mesh ?? null; n; n = n.parent) {
      const code = (n.metadata as { shelfCode?: unknown } | null)?.shelfCode;
      if (typeof code === "string") return code;
    }
    return null;
  }

  failed(code: string): boolean { return this.broken.has(code); }

  /** Plays the first clip of the model under the crosshair; at most one model moves (spec 2o §2.3). */
  hover(code: string | null): void {
    if (code === this.hovered) return;
    const prev = this.firstClip(this.hovered);
    if (prev) { prev.pause(); prev.goToFrame(prev.from); }
    this.hovered = code;
    this.firstClip(code)?.play(true);
  }

  private firstClip(code: string | null) {
    return code ? this.slots.find((s) => s.code === code)?.model?.groups[0] ?? null : null;
  }

  private render(): void {
    const gen = ++this.gen;
    this.hover(null);
    // a model already on the shelf moves to its new cell instead of loading again (24 GLBs on a phone)
    const loaded = new Map<string, Slot>();
    for (const s of this.slots) {
      if (s.model) { loaded.set(s.code, s); continue; }
      s.badge?.dispose(false, true); s.holder.dispose(false, true);
    }
    this.slots = [];
    const l = this.layout();
    this.page = l.page;
    this.room.emptySign.setEnabled(this.assets.length === 0);
    this.room.signs.prev.setEnabled(l.page > 0);
    this.room.signs.next.setEnabled(l.page < l.pages - 1);
    for (const cell of l.cells) {
      const kept = loaded.get(cell.asset.code);
      if (kept) {
        loaded.delete(cell.asset.code);
        this.slots.push(kept);
        this.fit(kept, cell.pos);
        continue;
      }
      const holder = new TransformNode(`shelf:${cell.asset.code}`, this.scene);
      holder.metadata = { shelfCode: cell.asset.code };
      const slot: Slot = { code: cell.asset.code, holder, model: null, badge: null };
      this.slots.push(slot);
      this.load(this.scene, cell.asset.code).then(
        (model) => {
          if (gen !== this.gen) { model.dispose(); return; }
          slot.model = model;
          this.broken.delete(slot.code);
          this.place(slot, model, cell.pos);
        },
        () => {
          if (gen !== this.gen) return;
          this.broken.add(slot.code);
          this.placeholder(slot, cell.pos);
        },
      );
    }
    for (const s of loaded.values()) { s.model?.dispose(); s.badge?.dispose(false, true); s.holder.dispose(false, true); }
  }

  private place(slot: Slot, model: LoadedModel, at: { x: number; y: number; z: number }): void {
    for (const m of roots(model)) m.parent = slot.holder;
    for (const m of model.meshes) { m.isPickable = true; m.checkCollisions = false; }
    const g = model.groups[0];
    if (g) { g.start(true); g.pause(); g.goToFrame(g.from); }
    this.fit(slot, at);
  }

  /** Scales the model into the cell at `at`, standing on its board, with its badge beside it. */
  private fit(slot: Slot, at: { x: number; y: number; z: number }): void {
    slot.holder.scaling.setAll(1);
    slot.holder.position.setAll(0);
    slot.holder.computeWorldMatrix(true);
    const { min, max } = slot.holder.getHierarchyBoundingVectors(true);
    const t = fitTransform(min, max, at, SHELF.slot);
    slot.holder.scaling.setAll(t.scale);
    slot.holder.position.set(t.x, t.y, t.z);
    slot.badge?.dispose(false, true);
    slot.badge = slot.model && slot.model.groups.length > 0 ? this.badge(slot.code, at) : null;
  }

  /** A small amber disc on the board's front corner: this model has animation (spec 2o §2.3). */
  private badge(code: string, at: { x: number; y: number; z: number }): AbstractMesh {
    const disc = MeshBuilder.CreateCylinder(`shelf:${code}:anim`, { diameter: 0.07, height: 0.01 }, this.scene);
    const mat = new StandardMaterial(`shelf:${code}:anim`, this.scene);
    mat.diffuseColor = Color3.FromHexString("#ffb300");
    mat.emissiveColor = Color3.FromHexString("#7a5200");
    disc.material = mat;
    disc.position.set(at.x + SHELF.cell / 2 - 0.06, at.y + 0.005, at.z - SHELF.depth / 2 + 0.06);
    disc.isPickable = false;
    return disc;
  }

  private placeholder(slot: Slot, at: { x: number; y: number; z: number }): void {
    const box = MeshBuilder.CreateBox(`shelf:${slot.code}:broken`, { size: 0.3 }, this.scene);
    const mat = new StandardMaterial(`shelf:${slot.code}:broken`, this.scene);
    mat.diffuseColor = Color3.FromHexString("#b0413e");
    box.material = mat;
    box.parent = slot.holder;
    box.isPickable = true;
    slot.holder.position.set(at.x, at.y + 0.15, at.z);
  }
}
